import { Box, ListItem, Text, UnorderedList, useToast } from "@chakra-ui/react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SourcingEventStatus,
  useBeginEvaluationMutation,
  useCompleteNegotiationRoundV2Mutation,
  useDispatchAwardLettersMutation,
  useCompleteSourcingEventV2Mutation,
  useConfirmAwardMutation,
  useReopenCompletedSourcingEventV2Mutation,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { formatTimeLeft } from "../../../../common/formatting/timeLeft";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { useCompleteEvaluationDescription } from "../evaluation/useCompleteEvaluationDescription";
import { bidDocuments } from "../award/bidDocuments";
import { CreateContractFromBidModal } from "../award/CreateContractFromBidModal";
import { requiresStandstill } from "../award/requiresStandstill";
import { StaleDecisionsBanner } from "../evaluation/staleScore";
import { scrollToEventTabs } from "../eventTabsAnchor";
import type { EventTab } from "../EventTabs";
import { useGoToEventTab } from "../useGoToEventTab";
import {
  deriveNowPanel,
  type NowAction,
  type NowActionTarget,
  type NowCommandTarget,
} from "./deriveNowPanel";
import { NowPanel, type NowPanelAction } from "./NowPanel";
import { useNowPanelData } from "./useNowPanelData";

export const EventNowPanel = ({
  event,
  activeTab,
}: {
  event: SourcingEventV2Dto;
  activeTab: EventTab;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger, triggerSoft } = useApiError();
  const confirmationModal = useConfirmationModal();
  const goToTab = useGoToEventTab();
  const completeEvaluationDescription = useCompleteEvaluationDescription(event);
  const [beginEvaluation, { isLoading: isBeginning }] = useBeginEvaluationMutation();
  const [completeEvaluation, { isLoading: isCompleting }] = useCompleteSourcingEventV2Mutation();
  const [reopenEvaluation, { isLoading: isReopening }] = useReopenCompletedSourcingEventV2Mutation();
  const [confirmAward, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const [completeRound, { isLoading: isCompletingRound }] = useCompleteNegotiationRoundV2Mutation();
  const [sendAwardLetters, { isLoading: isSendingLetters }] = useDispatchAwardLettersMutation();
  const { award, isAwarding, winnerIds } = useAwardSelection();
  const [contractBidId, setContractBidId] = useState<string | null>(null);
  const data = useNowPanelData(event, activeTab);
  const state = deriveNowPanel(data);
  const contractBid = data.bids.find((bid) => bid.id === contractBidId);

  const pending: Record<NowCommandTarget["command"], boolean> = {
    beginEvaluation: isBeginning,
    completeEvaluation: isCompleting,
    reopenEvaluation: isReopening,
    awardEvent: isAwarding,
    confirmAward: isConfirming,
    completeRound: isCompletingRound,
    createContract: false,
    sendAwardLetters: isSendingLetters,
  };

  const run = async (target: NowCommandTarget) => {
    try {
      switch (target.command) {
        case "createContract":
          setContractBidId(target.bidId);
          return;
        case "beginEvaluation":
          await beginEvaluation({ eventId: event.id }).unwrap();
          return;
        case "completeEvaluation":
          await completeEvaluation({ eventId: event.id }).unwrap();
          return;
        case "reopenEvaluation":
          await reopenEvaluation({ eventId: event.id }).unwrap();
          return;
        case "awardEvent":
          await award();
          return;
        case "completeRound":
          await completeRound({ eventId: event.id, roundId: target.roundId }).unwrap();
          toast({ title: t("Negotiation round completed"), status: "success", position: "top-right" });
          return;
        case "sendAwardLetters":
          await sendAwardLetters({ eventId: event.id }).unwrap();
          toast({ title: t("The award letters are sent"), status: "success", position: "top-right" });
          return;
        case "confirmAward":
          await confirmAward({ eventId: event.id }).unwrap();
          toast({ title: t("Award confirmed"), status: "success", position: "top-right" });
      }
    } catch (e) {
      if (target.command === "beginEvaluation") triggerSoft(e);
      else trigger(e);
    }
  };

  const confirmationFor = (
    command: NowCommandTarget["command"],
  ): { title: string; description: ReactNode } | null => {
    switch (command) {
      case "completeEvaluation":
        return {
          title: t("Complete the evaluation?"),
          description: completeEvaluationDescription(),
        };
      case "reopenEvaluation":
        return {
          title: t("Reopen the evaluation?"),
          description: t("The procurement is moved back to evaluation."),
        };
      case "completeRound":
        return {
          title: t("Complete negotiation round?"),
          description: t("Suppliers will no longer be able to submit bids for this round."),
        };
      case "awardEvent": {
        const winners = data.bids.filter((bid) => winnerIds.includes(bid.id));
        const withStandstill = requiresStandstill(event);
        return {
          title: t("Award to the selected suppliers?"),
          description: (
            <>
              <Text>{t("Do you want to award the contract to:")}</Text>
              <UnorderedList mt="2" mb={withStandstill ? "3" : undefined}>
                {winners.map((bid) => (
                  <ListItem key={bid.id}>{bid.ownerName}</ListItem>
                ))}
              </UnorderedList>
              {withStandstill && <Text>{t("Next you approve and send the award letters. The standstill period starts when they are sent.")}</Text>}
              <Box mt="3">
                <StaleDecisionsBanner
                  bids={data.evaluation?.bids ?? []}
                  evaluationCompleted={event.status === SourcingEventStatus.Evaluated}
                />
              </Box>
            </>
          ),
        };
      }
      case "sendAwardLetters":
        return {
          title: t("Send the award letters?"),
          description: requiresStandstill(event)
            ? t("Every supplier gets their letter and the standstill period starts. This cannot be undone.")
            : t("Every supplier gets their letter. This cannot be undone."),
        };
      case "confirmAward":
        return {
          title: t("Confirm the award?"),
          description: t("The award is final and the procurement moves on to the contract."),
        };
      default:
        return null;
    }
  };

  const go = (target: NowActionTarget) => {
    if (target.kind === "tab") {
      goToTab(target.tab, target.subTab, target.focusBidId, target.reveal, target.round);
      if (target.reveal == null) scrollToEventTabs();
      return;
    }
    if (target.kind === "url") {
      navigate(target.url);
      return;
    }
    const confirmation = confirmationFor(target.command);
    if (confirmation == null) {
      void run(target);
      return;
    }
    confirmationModal.show({ ...confirmation, onConfirmed: () => run(target) });
  };

  const toPanelAction = (candidate: NowAction): NowPanelAction => ({
    label: t(candidate.labelKey, candidate.labelParams ?? {}),
    icon: candidate.icon,
    onClick: () => go(candidate.target),
    isLoading: candidate.target.kind === "command" && pending[candidate.target.command],
  });

  if (state == null) return null;
  const { action, secondary } = state;

  return (
    <>
      {confirmationModal.modal}
      <NowPanel
        status={t(state.status.key, {
          ...state.status.params,
          ...(state.status.dateIso ? { date: displayDateWithTime(state.status.dateIso) } : {}),
          ...(state.status.timeLeft ? { timeLeft: formatTimeLeft(state.status.timeLeft, t) } : {}),
        })}
        detail={
          state.detail
            .map((segment) => [t(segment.key, segment.params ?? {}), segment.text].filter(Boolean).join(": "))
            .join(". ") || undefined
        }
        progress={state.progress}
        action={action && toPanelAction(action)}
        secondaryAction={secondary && toPanelAction(secondary)}
      />
      {contractBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractBid.id}
          supplierName={contractBid.ownerName}
          defaultTitle={event.eventData.title}
          documents={bidDocuments(contractBid)}
          isOpen={contractBidId !== null}
          onClose={() => setContractBidId(null)}
        />
      )}
    </>
  );
};
